import { useCallback, useEffect, useRef } from 'react';
import { SCROLL_ARIA_VALUE_MAX, SCROLL_ARIA_VALUE_MIN, SCROLL_BAR_LINE_ID } from '../constants';
import { findCommonParent, findLastScrollableElement, getFirstScrollableChild, getFirstScrollableElement, getScrollbarContainer, getScrollbarLine, getWidestScrollableChild, syncScrollbarAriaValue } from '../utils';
export default function useAddResizeObserver({ scrollbarPrefixId, scrollSpaceRef, scrollRatioRef, handleVerticalScroll, handleDragStart, handleUserWheelScroll, handleScrollbarKeyDown, lastScrollableElementRef }) {
    const previousScrollWidth = useRef(0);
    const previousScrollHeight = useRef(0);
    const previousContainerWidth = useRef(0);
    const previousContainerHeight = useRef(0);
    const createNewScrollLine = useCallback(() => {
        var _a;
        const container = getScrollbarContainer(scrollbarPrefixId);
        const oldElement = getScrollbarLine(scrollbarPrefixId);
        if (oldElement)
            oldElement.remove();
        const scrollLine = document.createElement('div');
        scrollLine.id = `${SCROLL_BAR_LINE_ID}${scrollbarPrefixId}`;
        scrollLine.className = SCROLL_BAR_LINE_ID;
        scrollLine.tabIndex = 0;
        scrollLine.setAttribute('role', 'scrollbar');
        scrollLine.setAttribute('aria-orientation', 'horizontal');
        scrollLine.setAttribute('aria-label', 'Horizontal scroll');
        scrollLine.setAttribute('aria-valuemin', String(SCROLL_ARIA_VALUE_MIN));
        scrollLine.setAttribute('aria-valuemax', String(SCROLL_ARIA_VALUE_MAX));
        scrollLine.setAttribute('aria-valuenow', String(SCROLL_ARIA_VALUE_MIN));
        scrollLine.addEventListener('mousedown', handleDragStart);
        scrollLine.addEventListener('keydown', handleScrollbarKeyDown);
        const firstScrollableElement = getFirstScrollableChild(container);
        const lastScrollableElement = findLastScrollableElement(container);
        lastScrollableElementRef.current = lastScrollableElement;
        if (firstScrollableElement && lastScrollableElement) {
            const parent = findCommonParent(firstScrollableElement, lastScrollableElement);
            if (parent) {
                if (parent.className.includes('header')) {
                    (_a = parent.parentElement) === null || _a === void 0 ? void 0 : _a.appendChild(scrollLine);
                }
                else {
                    parent.appendChild(scrollLine);
                }
            }
            handleVerticalScroll();
        }
        return scrollLine;
    }, [handleVerticalScroll, handleDragStart, handleScrollbarKeyDown, lastScrollableElementRef, scrollbarPrefixId]);
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            var _a, _b, _c, _d, _e;
            const container = getScrollbarContainer(scrollbarPrefixId);
            const widestScrollableElement = getWidestScrollableChild(container);
            const currentScrollWidth = (widestScrollableElement === null || widestScrollableElement === void 0 ? void 0 : widestScrollableElement.scrollWidth) || 0;
            const currentScrollHeight = (widestScrollableElement === null || widestScrollableElement === void 0 ? void 0 : widestScrollableElement.scrollHeight) || 0;
            if (currentScrollWidth !== previousScrollWidth.current ||
                previousContainerWidth.current !== (container === null || container === void 0 ? void 0 : container.clientWidth)) {
                previousScrollWidth.current = currentScrollWidth;
                previousContainerWidth.current = (_a = container === null || container === void 0 ? void 0 : container.clientWidth) !== null && _a !== void 0 ? _a : 0;
                if (widestScrollableElement) {
                    const scrollLine = getScrollbarLine(scrollbarPrefixId) || createNewScrollLine();
                    const isRelative = scrollLine.style.position === 'relative';
                    const { clientWidth, scrollWidth } = widestScrollableElement;
                    const containerWidth = (_b = container === null || container === void 0 ? void 0 : container.clientWidth) !== null && _b !== void 0 ? _b : 0;
                    const thumbWidth = Math.max(Math.round((clientWidth / scrollWidth) * containerWidth), 1);
                    scrollRatioRef.current = containerWidth > 0 ? scrollWidth / containerWidth : 1;
                    scrollSpaceRef.current = containerWidth - thumbWidth;
                    scrollLine.style.width = `${thumbWidth}px`;
                    scrollLine.style.left = `${isRelative ? 0 : (_c = container === null || container === void 0 ? void 0 : container.getBoundingClientRect().x) !== null && _c !== void 0 ? _c : 0}px`;
                    widestScrollableElement.scrollLeft = 0;
                    syncScrollbarAriaValue(scrollLine, widestScrollableElement);
                    widestScrollableElement.addEventListener('scroll', handleUserWheelScroll);
                }
                else {
                    (_d = getScrollbarLine(scrollbarPrefixId)) === null || _d === void 0 ? void 0 : _d.remove();
                }
            }
            if (currentScrollHeight !== previousScrollHeight.current ||
                previousContainerHeight.current !== (container === null || container === void 0 ? void 0 : container.clientHeight)) {
                previousScrollHeight.current = currentScrollHeight;
                previousContainerHeight.current = (_e = container === null || container === void 0 ? void 0 : container.clientHeight) !== null && _e !== void 0 ? _e : 0;
                const firstVerticalScroll = getFirstScrollableElement(container);
                firstVerticalScroll === null || firstVerticalScroll === void 0 ? void 0 : firstVerticalScroll.addEventListener('scroll', handleVerticalScroll);
                handleVerticalScroll();
            }
        });
        const container = getScrollbarContainer(scrollbarPrefixId);
        const widestScrollableElement = getWidestScrollableChild(container);
        const scrollLine = getScrollbarLine(scrollbarPrefixId);
        const table = container === null || container === void 0 ? void 0 : container.querySelector('table');
        if (table && container) {
            resizeObserver.observe(table);
            resizeObserver.observe(container);
        }
        widestScrollableElement === null || widestScrollableElement === void 0 ? void 0 : widestScrollableElement.addEventListener('scroll', handleUserWheelScroll);
        scrollLine === null || scrollLine === void 0 ? void 0 : scrollLine.addEventListener('mousedown', handleDragStart);
        scrollLine === null || scrollLine === void 0 ? void 0 : scrollLine.addEventListener('keydown', handleScrollbarKeyDown);
        return () => {
            const firstVerticalScroll = getFirstScrollableElement(container);
            firstVerticalScroll === null || firstVerticalScroll === void 0 ? void 0 : firstVerticalScroll.removeEventListener('scroll', handleVerticalScroll);
            resizeObserver.disconnect();
            scrollLine === null || scrollLine === void 0 ? void 0 : scrollLine.removeEventListener('mousedown', handleDragStart);
            scrollLine === null || scrollLine === void 0 ? void 0 : scrollLine.removeEventListener('keydown', handleScrollbarKeyDown);
            widestScrollableElement === null || widestScrollableElement === void 0 ? void 0 : widestScrollableElement.removeEventListener('scroll', handleUserWheelScroll);
        };
    });
}
